import {
  Box,
  Typography,
  Chip,
  Divider,
  Skeleton,
  Tooltip,
  Paper,
  Grid,
  TextField,
  IconButton,
  Snackbar,
  Alert,
} from "@mui/material";
import {
  RouterOutlined,
  GroupsOutlined,
  LabelOutlined,
  SettingsInputComponentOutlined,
  DnsOutlined,
  ContentCopy,
} from "@mui/icons-material";
import { lighten, useTheme } from "@mui/material/styles";
import useStyles from "./styles";
import { useState } from "react";

// Mapeamento de ícones por seção
const SECTION_ICONS = {
  nome: <DnsOutlined fontSize="small" />,
  ip: <RouterOutlined fontSize="small" />,
  grupos: <GroupsOutlined fontSize="small" />,
  etiquetas: <LabelOutlined fontSize="small" />,
  macros: <SettingsInputComponentOutlined fontSize="small" />,
};

// Cabeçalho
function SectionHeader({ icon, label }) {
  const theme = useTheme();
  const styles = useStyles(theme);
  return (
    <Box sx={styles.headerContainer}>
      <Box sx={styles.headerIcon}>{icon}</Box>
      <Typography variant="caption" sx={styles.headerLabel}>
        {`${label}: `}
      </Typography>
    </Box>
  );
}

// (NOME, IP)
function InfoField({ icon, label, value, loading, setSnackbar }) {
  const theme = useTheme();
  const styles = useStyles(theme);
  return (
    <Box sx={styles.infoSection}>
      <SectionHeader icon={icon} label={label} />
      {loading ? (
        <Skeleton variant="text" width={160} height={22} />
      ) : (
        <Tooltip
          title={
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              {value}
              <IconButton
                size="small"
                color="inherit"
                onClick={() =>
                  CopyTexto({ campo: value, setSnackbar: setSnackbar })
                }
                sx={{ p: 0 }}
              >
                <ContentCopy fontSize="inherit" />
              </IconButton>
            </Box>
          }
          arrow
        >
          <Typography variant="subtitle" sx={styles.infoValue} noWrap>
            {value || "—"}
          </Typography>
        </Tooltip>
      )}
    </Box>
  );
}

// Chips (GRUPOS, ETIQUETAS, MACROS)
const ChipField = ({
  icon,
  label,
  items,
  loading,
  renderChip,
  tooltip,
  grid = false,
}) => {
  const theme = useTheme();
  const styles = useStyles(theme);
  return (
    <Box
      sx={{
        ...styles.infoSection,
        ...(grid ? { flexDirection: "column" } : {}),
      }}
    >
      <SectionHeader icon={icon} label={label} />
      {loading ? (
        <Box sx={styles.loadingBox}>
          {[1, 2].map((i) => (
            <Skeleton key={i} variant="rounded" width={80} height={24} />
          ))}
        </Box>
      ) : items?.length ? (
        <Tooltip title={tooltip || ""} arrow disableHoverListener={!tooltip}>
          {grid ? (
            <Grid container spacing={1}>
              {items?.map((item, idx) => renderChip(item, idx))}
            </Grid>
          ) : (
            <Box sx={styles.chipContainer}>
              {items?.map((item, idx) => renderChip(item, idx))}
            </Box>
          )}
        </Tooltip>
      ) : (
        <Typography variant="body2" sx={styles.emptyText}>
          Nenhum
        </Typography>
      )}
    </Box>
  );
};

async function CopyTexto({ campo, setSnackbar }) {
  try {
    await navigator.clipboard.writeText(campo);
  } catch (err) {
    const textArea = document.createElement("textarea");
    textArea.value = campo;
    document.body.appendChild(textArea);
    textArea.select();
    try {
      document.execCommand("copy");
    } catch (err) {
      console.error("Falha ao copiar texto: ", err);
    }
    document.body.removeChild(textArea);
  }
  setSnackbar({
    children: "Campo copiado!",
    severity: "info",
  });
}

export default function HostInfo({ hostDetails }) {
  const theme = useTheme();
  const styles = useStyles(theme);
  const [snackbar, setSnackbar] = useState(false);

  const colors = [
    theme.palette.cardTrafficData.paperColor.outputData,
    theme.palette.colors.blue.medium,
    theme.palette.colors.orange.dark,
    lighten(theme.palette.cardTrafficData.paperColor.speeData, 0.3),
    theme.palette.colors.blue.darker,
    lighten(theme.palette.colors.pink.dark, 0.3),
    theme.palette.colors.purple.medium,
    theme.palette.colors.yellow.dark,
  ];

  // Estado de carregamento: host selecionado mas detalhes ainda não chegaram
  const loading = hostDetails === null;

  return (
    <Paper elevation={0} sx={styles.paper}>
      {/* Título do painel */}
      <Typography variant="subtitle" sx={styles.title}>
        Informações do Host
      </Typography>
      <Divider sx={styles.divider} />
      <Grid container spacing={1}>
        <Grid item xs={12} sm={8}>
          <Paper sx={styles.cardHostInfo}>
            <Grid container spacing={1}>
              <Grid item xs={12} sm={6}>
                {/* NOME */}
                <InfoField
                  icon={SECTION_ICONS.nome}
                  label="Nome"
                  value={hostDetails?.nome}
                  loading={loading}
                  setSnackbar={setSnackbar}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                {/* IP */}
                <InfoField
                  icon={SECTION_ICONS.ip}
                  label="IP"
                  value={hostDetails?.ip}
                  loading={loading}
                  setSnackbar={setSnackbar}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                {/* GRUPO */}
                <ChipField
                  icon={SECTION_ICONS?.grupos}
                  label="Grupo"
                  items={hostDetails?.grupos}
                  loading={loading}
                  renderChip={(grupo, idx) => (
                    <Chip
                      key={grupo.groupid ?? idx}
                      label={grupo.name}
                      size="small"
                      sx={{ ...styles.chipGroup, backgroundColor: colors[idx] }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                {/* ETIQUETAS */}
                <ChipField
                  icon={SECTION_ICONS.etiquetas}
                  label="Etiquetas"
                  items={hostDetails?.etiquetas}
                  loading={loading}
                  renderChip={(etiqueta, idx) => (
                    <Chip
                      label={`${etiqueta.tag}${etiqueta.value ? `: ${etiqueta.value}` : ""}`}
                      size="small"
                      sx={{ ...styles.chipTag, backgroundColor: colors[idx] }}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={4}>
          <Paper sx={styles.cardHostInfo}>
            <Grid container spacing={1}>
              <Grid item xs={12}>
                {/* MACROS */}

                <ChipField
                  icon={SECTION_ICONS.macros}
                  label="Macros"
                  items={hostDetails?.macros}
                  loading={loading}
                  grid={true}
                  renderChip={(macro, idx) => (
                    <Grid item sm={12} md={6}>
                      <Box sx={styles.boxMacro}>
                        <Typography
                          variant="caption"
                          sx={{
                            ...styles.headerLabel,
                            color:
                              theme.palette.mode === "dark"
                                ? theme.palette.primary.light
                                : theme.palette.primary.dark,
                          }}
                        >
                          {`${macro?.macro}: `}
                        </Typography>
                        <Tooltip
                          title={
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              {macro?.value}
                              <IconButton
                                size="small"
                                color="inherit"
                                onClick={() =>
                                  CopyTexto({
                                    campo: macro?.value,
                                    setSnackbar,
                                  })
                                }
                                sx={{ p: 0 }}
                              >
                                <ContentCopy fontSize="inherit" />
                              </IconButton>
                            </Box>
                          }
                          interactive
                          arrow
                          disableHoverListener={!macro?.value}
                        >
                          <Typography
                            variant="caption"
                            sx={styles.infoValue}
                            noWrap
                          >
                            {macro?.value}
                          </Typography>
                        </Tooltip>
                      </Box>
                    </Grid>
                  )}
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
      {!!snackbar && (
        <Snackbar
          open
          onClose={() => setSnackbar(null)}
          autoHideDuration={snackbar?.severity == "success" ? 6000 : 3000}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert {...snackbar} onClose={() => setSnackbar(null)} />
        </Snackbar>
      )}
    </Paper>
  );
}
